<script lang="ts">
  import { dailyUsage, usageLimits } from '$lib/stores/subscription';
  import { useCountdown } from '$lib/hooks/useTimer';
  import { useError } from '$lib/hooks/useError';
  
  interface Props {
    details?: string;
  }
  
  let { details = '' }: Props = $props();
  
  // Error handling setup
  const { handleError } = useError('LimitReachedPrompt');
  
  // Parse rate limit details if it's JSON
  // Default to free limit (5) as fallback
  let rateLimit = $state({ used: 5, limit: 5, resetIn: 60 });
  let isRateLimit = $state(false);
  
  // State for countdown timer
  let remainingSeconds = $state(60);
  
  // Initialize rate limit data from details
  $effect(() => {
    if (details) {
      try {
        const parsed = JSON.parse(details);
        if (parsed.type === 'rate') {
          rateLimit = parsed;
          isRateLimit = true;
          // Update remaining seconds and restart countdown with new value
          const resetTime = parsed.resetIn || 60;
          remainingSeconds = resetTime;
          countdown.reset(resetTime);
          countdown.start();
        }
      } catch (error) {
        // Silent error handling for JSON parsing - fallback to old format check
        handleError(error, {
          severity: 'info',
          context: { action: 'parseRateLimitDetails', details },
          showToUser: false,
          logToConsole: false
        });
        
        // Check if it's the old format
        const isOldRateLimit = details?.includes('분당');
        if (isOldRateLimit !== isRateLimit) {
          isRateLimit = isOldRateLimit;
          if (isOldRateLimit) {
            // Start countdown for old format rate limit
            remainingSeconds = 60;
            countdown.reset(60);
            countdown.start();
          } else {
            countdown.stop();
          }
        }
      }
    } else {
      // No details, stop countdown if running
      if (isRateLimit) {
        isRateLimit = false;
        countdown.stop();
      }
    }
  });
  
  // Use countdown hook for rate limit timer - initialize with default
  const countdown = useCountdown(
    60, // Default value, will be updated in effect
    () => {
      // Countdown complete
    },
    false // Don't auto-start here, we'll control it
  );
  
  // Update remaining seconds when countdown changes
  countdown.onUpdate((time) => {
    remainingSeconds = time;
  });
  
  // Start countdown is now handled in the details parsing effect above
  // This effect is removed to avoid duplicate countdown management
</script>

<div class="prompt-content">
  {#if isRateLimit}
    <p>잠시 너무 많은 요청을 보내셨습니다.</p>
    <div class="usage-stats">
      <div class="stat">
        <span class="stat-label">분당 한도</span>
        <span class="stat-value exceeded">{rateLimit.used}/{rateLimit.limit}회</span>
      </div>
      <div class="stat">
        <span class="stat-label">대기 시간</span>
        <span class="stat-value timer">{remainingSeconds}초</span>
      </div>
    </div>
    <p class="reset-info">잠시 후 다시 사용할 수 있습니다.</p>
  {:else}
    <p>오늘의 무료 사용 한도에 도달했습니다.</p>
    <div class="usage-stats">
      <div class="stat {$dailyUsage.spellChecks >= $usageLimits.spellCheck ? 'exceeded' : ''}">
        <span class="stat-label">맞춤법 검사</span>
        <span class="stat-value">{$dailyUsage.spellChecks}/{$usageLimits.spellCheck}회</span>
      </div>
      <div class="stat {$dailyUsage.aiAnalyses >= $usageLimits.aiAnalysis ? 'exceeded' : ''}">
        <span class="stat-label">AI 분석</span>
        <span class="stat-value">{$dailyUsage.aiAnalyses}/{$usageLimits.aiAnalysis}회</span>
      </div>
    </div>
    <p class="reset-info">한국 시간 자정에 사용 횟수가 초기화됩니다.</p>
  {/if}
</div>

<style>
  .prompt-content p {
    margin: 0 0 8px 0;
    color: var(--text-secondary);
    font-size: 13px;
    line-height: 1.5;
  }


  .usage-stats {
    display: flex;
    gap: 20px;
    background: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    border-radius: 8px;
    padding: 12px 16px;
    margin: 12px 0;
  }

  .stat {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  .stat-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary);
  }

  .stat-value {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .stat.exceeded .stat-value {
    color: var(--error);
  }

  .stat-value.exceeded {
    color: var(--error);
  }

  .stat-value.timer {
    color: var(--warning, #f59e0b);
    font-weight: 700;
  }

  .reset-info {
    font-size: 11px;
    color: var(--text-tertiary);
    margin-top: 8px;
  }
</style>